import React, { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, Search, Heart, Calendar, User, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme, currentBrandSlug } from "@/contexts/ThemeProvider";
import { useAuth } from "@/contexts/AuthProvider";
import { useFavourites } from "@/contexts/FavouritesProvider";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogHeader, DialogDescription } from "@/components/ui/dialog";
import BrandButton from "@/components/brand/BrandButton";
import BrandInput, { BrandField } from "@/components/brand/BrandInput";
import BrandLabel from "@/components/brand/BrandLabel";
import { BRAND_INFO } from "@/lib/themes";

const NAV_LINKS_BY_BRAND = {
  gmh: [
    { to: "/", label: "Home" },
    { to: "/castlewood", label: "Castlewood" },
    { to: "/vela", label: "Vela" },
    { to: "/brionj", label: "Brionj" },
    // Note: Search is intentionally NOT in this array. Desktop renders it as
    // a dedicated icon-button on the right (see `nav-search-cta` below), and
    // the mobile sheet renders a hardcoded Search row alongside Favourites /
    // Your Stays / Admin. Including it here would duplicate it on mobile.
  ],
  castlewood: [
    { to: "/castlewood", label: "Home" },
    { to: "/castlewood/search", label: "Hotels" },
    { to: "/", label: "GMH" },
    // Note: "Your Stays" intentionally omitted — the mobile sheet renders a
    // hardcoded Your Stays row below (alongside Search / Favourites / Admin),
    // and the desktop nav surfaces it as a right-side icon link. Listing it
    // here would duplicate it on mobile.
  ],
  vela: [
    { to: "/vela", label: "Home" },
    { to: "/vela/search", label: "Stays" },
    { to: "/", label: "GMH" },
    // Note: "Your Stays" intentionally omitted — see castlewood comment above.
  ],
  brionj: [
    { to: "/brionj", label: "Collection" },
    { to: "/brionj/collection", label: "Residences" },
    { to: "/bookings", label: "Enquiries" },
    { to: "/", label: "GMH" },
  ],
  admin: [
    { to: "/admin", label: "Dashboard" },
    { to: "/", label: "Exit Admin" },
  ],
};

const BrandWordmark = ({ slug }) => {
  if (slug === "gmh")
    return (
      <Link to="/" className="font-display text-[18px] font-[600] border-[1.5px] border-current px-3 py-1.5 tracking-[0.12em] uppercase" data-testid="brand-wordmark-gmh">
        GMH
      </Link>
    );
  if (slug === "castlewood")
    return (
      <Link to="/castlewood" className="font-display text-[22px] font-[700] tracking-tight" data-testid="brand-wordmark-castlewood">
        Castlewood
      </Link>
    );
  if (slug === "vela")
    return (
      <Link to="/vela" className="font-display text-[28px] font-[500] italic" data-testid="brand-wordmark-vela" style={{ fontFamily: "var(--font-accent)" }}>
        Vela
      </Link>
    );
  if (slug === "brionj")
    return (
      <Link to="/brionj" className="font-display italic text-[26px] tracking-tight text-[hsl(var(--brand-accent))]" data-testid="brand-wordmark-brionj">
        Brionj
      </Link>
    );
  if (slug === "admin")
    return (
      <Link to="/admin" className="font-display text-[16px] font-[600] tracking-[0.12em] uppercase" data-testid="brand-wordmark-admin">
        GMH OPS
      </Link>
    );
  return null;
};

const SignInDialogInner = ({ onClose }) => {
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await signIn(email, name);
      onClose?.();
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="p-6">
      <DialogHeader>
        <DialogTitle className="font-display text-[28px]">Sign in</DialogTitle>
        <DialogDescription className="font-body text-[14px] text-muted-foreground">
          Use any email — this is a demo prototype.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
        <BrandField label="Email" htmlFor="signin-email" required>
          <BrandInput id="signin-email" data-testid="signin-email-input" type="email" value={email} placeholder="you@example.com" onChange={(e) => setEmail(e.target.value)} required />
        </BrandField>
        <BrandField label="Name (optional)" htmlFor="signin-name">
          <BrandInput id="signin-name" data-testid="signin-name-input" value={name} placeholder="Jane Traveller" onChange={(e) => setName(e.target.value)} />
        </BrandField>
        <div className="flex flex-col gap-2">
          <BrandButton type="submit" disabled={loading} data-testid="signin-submit">{loading ? "Signing in…" : "Sign in"}</BrandButton>
          <button type="button" className="text-[12px] text-muted-foreground underline" onClick={() => { setEmail("anna.berg@example.com"); setName("Anna Berg"); }} data-testid="signin-demo-fill">
            Use demo account (anna.berg@example.com)
          </button>
        </div>
      </form>
    </div>
  );
};

const BrandNav = () => {
  const { theme } = useTheme();
  const slug = currentBrandSlug(theme);
  const { user, signOut } = useAuth();
  const { ids } = useFavourites();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const isBrionj = slug === "brionj";
  const isAdmin = slug === "admin";
  const links = NAV_LINKS_BY_BRAND[slug] || NAV_LINKS_BY_BRAND.gmh;

  const navBg = slug === "castlewood"
    ? "bg-[#EDE6D6]"
    : isBrionj
    ? "bg-[hsl(var(--background))]"
    : "bg-[hsl(var(--background))]";
  const ruleBelow = (slug === "castlewood" || isBrionj) ? "border-b border-[hsl(var(--brand-accent))]/40" : "border-b border-border";

  return (
    <header className={cn("sticky top-0 z-40", navBg, ruleBelow)} data-testid="brand-nav">
      <div className="container-rail flex items-center justify-between h-16 md:h-20">
        <div className="flex items-center gap-6">
          <BrandWordmark slug={slug} />
          {!isAdmin && (
            <div className="hidden md:flex items-center gap-6 ml-4">
              {links.slice(0, 4).map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to.length <= 2}
                  className={({ isActive }) =>
                    cn(
                      "text-[12px] font-ui transition-colors hover:text-foreground",
                      slug === "castlewood" && "smallcaps font-display tracking-[0.18em]",
                      slug === "brionj" && "smallcaps-wide text-[11px]",
                      slug === "vela" && "tracking-[0.04em]",
                      isActive ? "text-foreground" : "text-muted-foreground"
                    )
                  }
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!isAdmin && (
            <button onClick={() => navigate("/search")} className="hidden md:inline-flex items-center gap-2 text-[12px] text-muted-foreground hover:text-foreground px-3 py-2 transition-colors" data-testid="nav-search-cta">
              <Search size={14} /> Search
            </button>
          )}
          {!isAdmin && (
            <Link to="/favourites" className="hidden md:inline-flex items-center gap-2 text-[12px] text-muted-foreground hover:text-foreground px-3 py-2 transition-colors" data-testid="nav-favourites">
              <Heart size={14} />
              <span className="hidden lg:inline">Favourites{ids.length ? ` (${ids.length})` : ""}</span>
            </Link>
          )}
          {!isAdmin && (
            <Link to="/bookings" className="hidden md:inline-flex items-center gap-2 text-[12px] text-muted-foreground hover:text-foreground px-3 py-2 transition-colors" data-testid="nav-bookings">
              <Calendar size={14} />
              <span className="hidden lg:inline">Your Stays</span>
            </Link>
          )}
          {!isAdmin && (
            <Link
              to="/admin"
              className={cn(
                "hidden md:inline-flex items-center font-ui font-medium text-muted-foreground/70 hover:text-foreground transition-colors uppercase border-l border-border/60 pl-3 ml-1 py-2",
                slug === "castlewood" && "font-display tracking-[0.22em] text-[11px]",
                slug === "brionj" && "tracking-[0.36em] text-[10px]",
                (slug === "vela" || slug === "gmh") && "tracking-[0.18em] text-[11px]"
              )}
              data-testid="nav-admin"
            >
              Admin
            </Link>
          )}
          {isAdmin && (
            <Link
              to="/"
              className="hidden md:inline-flex items-center justify-center transition-colors duration-200 uppercase font-medium border-[1.5px] border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white"
              style={{
                fontSize: "11px",
                letterSpacing: "0.24em",
                padding: "8px 14px",
              }}
              data-testid="nav-return-gmh"
            >
              ← Return to GMH
            </Link>
          )}

          {user ? (
            <div className="hidden md:flex items-center gap-2" data-testid="nav-user-block">
              <span className="text-[12px] text-muted-foreground">{user.name}</span>
              <button onClick={signOut} className="text-[12px] underline text-muted-foreground hover:text-foreground" data-testid="nav-signout">Sign out</button>
            </div>
          ) : (
            <Dialog open={signInOpen} onOpenChange={setSignInOpen}>
              <DialogTrigger asChild>
                <button className="hidden md:inline-flex items-center gap-2 text-[12px] font-medium px-3 py-2 hover:text-foreground transition-colors" data-testid="nav-signin">
                  <User size={14} /> Sign in
                </button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[440px] p-0">
                <SignInDialogInner onClose={() => setSignInOpen(false)} />
              </DialogContent>
            </Dialog>
          )}

          {/* CTA: brand-aware primary action */}
          {slug === "castlewood" && (
            <Link
              to="/castlewood/search"
              className="hidden md:inline-flex items-center justify-center transition-colors duration-200 uppercase font-medium border-[1.5px] border-[#7A2828] text-[#7A2828] hover:bg-[#7A2828] hover:text-[#FAF6EC]"
              style={{
                fontSize: "11px",
                letterSpacing: "0.36em",
                padding: "12px 20px",
              }}
              data-testid="nav-cta"
            >
              Find a Hotel
            </Link>
          )}
          {slug === "vela" && (
            <BrandButton to="/vela/search" size="sm" className="hidden md:inline-flex" data-testid="nav-cta">
              Find Your Stay
            </BrandButton>
          )}
          {slug === "brionj" && (
            <BrandButton to="/brionj/collection" size="sm" variant="secondary" className="hidden md:inline-flex" data-testid="nav-cta">
              Enquire
            </BrandButton>
          )}
          {slug === "gmh" && (
            <BrandButton to="/search" size="sm" className="hidden md:inline-flex" data-testid="nav-cta">
              Find a Stay
            </BrandButton>
          )}

          {/* Mobile menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="md:hidden p-2 -mr-2" data-testid="nav-mobile-toggle" aria-label="Open menu">
                <Menu size={22} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[90vw] max-w-[400px]">
              <SheetHeader>
                <SheetTitle className="font-display text-[20px]">{BRAND_INFO[slug]?.fullName || "GMH"}</SheetTitle>
              </SheetHeader>
              {/* The mobile sheet renders the brand's primary nav links first,
                  then a fixed set of utility rows (Search / Favourites / Your
                  Stays / Admin). Utility rows whose destination is already in
                  the brand's primary nav are filtered out to avoid duplicates
                  — e.g. Brionj's "Enquiries" already routes to /bookings, so
                  the generic "Your Stays" row is suppressed. */}
              <div className="mt-6 flex flex-col gap-1">
                {links.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        "px-2 py-3 text-[15px] font-ui border-b border-border transition-colors",
                        isActive ? "text-foreground" : "text-muted-foreground"
                      )
                    }
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {l.label}
                  </NavLink>
                ))}
                {(() => {
                  const primaryPaths = new Set(links.map((l) => l.to));
                  const utilityRows = [
                    { to: "/search",     label: "Search",     testid: "mobile-nav-search" },
                    { to: "/favourites", label: "Favourites", testid: "mobile-nav-favourites" },
                    { to: "/bookings",   label: "Your Stays", testid: "mobile-nav-bookings" },
                    { to: "/admin",      label: "Admin",      testid: "mobile-nav-admin", muted: true },
                  ];
                  return utilityRows
                    .filter((r) => !primaryPaths.has(r.to))
                    .map((r) => (
                      <Link
                        key={r.to}
                        to={r.to}
                        onClick={() => setOpen(false)}
                        className={cn(
                          "px-2 py-3 text-[15px] border-b border-border",
                          r.muted && "text-muted-foreground"
                        )}
                        data-testid={r.testid}
                      >
                        {r.label}
                      </Link>
                    ));
                })()}
                <div className="mt-6">
                  {user ? (
                    <BrandButton onClick={() => { signOut(); setOpen(false); }} variant="secondary" className="w-full" data-testid="mobile-signout">Sign out</BrandButton>
                  ) : (
                    <BrandButton onClick={() => { setOpen(false); setSignInOpen(true); }} className="w-full" data-testid="mobile-signin">Sign in</BrandButton>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default BrandNav;
